import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Modal, Form, Input, Checkbox, message, Popconfirm, Card, Tag, Divider, Spin, Select, Flex } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { getRoleList, createRole, updateRole, deleteRole, getRoleDetail } from '../../api/role';
import { getPermissionList, createPermission, updatePermission, deletePermission, getPermissionDetail } from '../../api/permission';
import type { CreateRoleRequest, UpdateRoleRequest, GetRoleListRequest, Role, RolePermission } from '../../DTO/role';
import type { Permission, CreatePermissionRequest } from '../../DTO/permission';
import { AppConfig } from '@/const/app';
import { NativeAddress } from '@packet/constants/AppTokenWhitelist';
import { bscTestnet, SupportedChainId, SupportedChainMap } from '@packet/constants/AppSupportChains';
import { getAllPoolsData, PoolWithAsset } from '@/hooks/useContractData';
import { toHex } from 'viem';

const { Title, Text } = Typography;

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [devSearchId, setDevSearchId] = useState<string>('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 权限弹窗相关状态
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [permissionForm] = Form.useForm();
  const [permissionConfirmLoading, setPermissionConfirmLoading] = useState(false);

  // 获取角色列表
  const fetchRoleList = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const params: GetRoleListRequest = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        order: 'desc',
      };

      const response = await getRoleList(params);
      if (response.code === 0) {
        setRoles(response.data.data);
        setPagination((prev) => ({
          ...prev,
          pageSize: pageSize,
          current: page,
          total: response.data.total,
        }));
      } else {
        message.error(response.message || '获取角色列表失败');
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
      message.error('获取角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取权限列表
  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const response = await getPermissionList({
        offset: 0,
        limit: 1000, // 获取所有权限
      });
      if (response.code === 0) {
        setPermissions(response.data.data.sort((a, b) => a.uri.localeCompare(b.uri)));
      } else {
        message.error(response.message || '获取权限列表失败');
      }
    } catch (error) {
      console.error('获取权限列表失败:', error);
      message.error('获取权限列表失败');
    } finally {
      setPermissionsLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchRoleList();
    fetchPermissions();
  }, []);

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    fetchRoleList(pagination.current, pagination.pageSize);
  };

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (name: string) => (
        <Space>
          <SafetyCertificateOutlined />
          <strong>{name}</strong>
        </Space>
      ),
    },
    {
      title: '权限描述',
      dataIndex: 'describe',
      key: 'describe',
      render: (describe: string) => describe,
    },
    {
      title: '权限数量',
      dataIndex: 'permissions',
      key: 'permissionCount',
      render: (permissions: RolePermission[]) => <Tag color="blue">{permissions?.length || 0} 个权限</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: number, record: Role) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除这个角色吗？" description="删除后该角色下的用户将失去相关权限" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role_name,
      status: role.status,
      permission_ids: role.permissions?.map((p) => p.id) || [],
      describe: role.describe,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteRole({ id });
      if (response.code === 0) {
        message.success('角色删除成功');
        // 重新获取角色列表
        fetchRoleList(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '删除角色失败');
      }
    } catch (error) {
      console.error('删除角色失败:', error);
      message.error('删除角色失败');
    }
  };

  const handleSubmit = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();

      if (editingRole) {
        // 编辑角色
        const updateData: UpdateRoleRequest = {
          id: editingRole.id,
          role_name: values.role_name,
          status: values.status,
          permission_ids: values.permission_ids,
          describe: values.describe,
        };

        const response = await updateRole(updateData);
        if (response.code === 0) {
          message.success('角色信息更新成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取角色列表
          fetchRoleList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '更新角色失败');
        }
      } else {
        // 新增角色
        const createData: CreateRoleRequest = {
          role_name: values.role_name,
          permission_ids: values.permission_ids,
          describe: values.describe,
        };

        const response = await createRole(createData);
        if (response.code === 0) {
          message.success('角色创建成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取角色列表
          fetchRoleList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '创建角色失败');
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  // 添加权限弹窗相关处理函数
  const handleAddPermission = () => {
    permissionForm.resetFields();
    setIsPermissionModalOpen(true);
  };

  const devDeletePermission = async (permission: Permission) => {
    const isOk = window.confirm(`确认删除权限：${permission.permission_name}(${permission.flag}) 吗？`);
    if (!isOk) return;

    try {
      const response = await deletePermission({ id: permission.id });
      if (response.code === 0) {
        message.success(`权限删除成功：${permission.permission_name}`);
      } else {
        message.error(response.message || '删除权限失败');
      }
    } catch (error) {
      console.error('删除权限失败:', error);
      message.error('删除权限失败');
    }
  };
  const addPermissionAuto = async () => {
    if (permissions.length === 0) return;

    try {
      // 使用动态获取的 token pool 数据
      const tokenPoolData = await getAllPoolsData();

      const createIt = async (token: PoolWithAsset) => {
        const chain = SupportedChainMap[token.chain.id as SupportedChainId];
        let chainName: string = chain.name;
        let tokenName = token.symbol;

        if (chain.id === bscTestnet.id) chainName = 'BSCTestnet';
        if (token.token === NativeAddress) tokenName = chain.nativeCurrency.symbol;

        const createOrUpdate = async (flag: string, permission_name: string, uri: string) => {
          const find = permissions.find((p) => p.flag === flag || p.permission_name === permission_name || p.uri === uri);
          if (find) {
            if (find.permission_name === permission_name && find.uri === uri && find.flag === flag) return;
            const isOk = window.confirm(`确认编辑权限：${permission_name}(${flag}) 吗？原数据：${find.permission_name}(${find.flag})`);
            if (!isOk) throw new Error('用户取消编辑权限');
            const response = await updatePermission({ id: find.id, permission_name, uri, flag });
            if (response.code === 0) {
              message.success(`权限编辑成功：${permission_name}`);
            } else {
              message.error(response.message || '编辑权限失败');
            }
          } else {
            const isOk = window.confirm(`确认创建权限：${permission_name}(${flag}) 吗？`);
            if (!isOk) throw new Error('用户取消创建权限');
            const response = await createPermission({ permission_name, uri, flag });
            if (response.code === 0) {
              message.success(`权限创建成功：${permission_name}`);
            } else {
              message.error(response.message || '创建权限失败');
            }
          }
        };
        console.log('token', tokenName, chainName, token);
        await createOrUpdate(`asset:${toHex(token.poolId, { size: 32 })}:${token.chain.id}`, `查看${tokenName}(${chainName})`, `/asset/${chainName}/${tokenName}`);
        await createOrUpdate(`freeze:${toHex(token.poolId, { size: 32 })}:${token.chain.id}`, `冻结${tokenName}(${chainName})`, `/freeze/${chainName}/${tokenName}`);
      };

      // 遍历所有动态获取的 token pool 数据
      for (const token of tokenPoolData) {
        await createIt(token);
      }

      // 重新获取权限列表
      await fetchPermissions();
    } catch (error) {
      console.error('自动创建权限失败:', error);
      message.error('自动创建权限失败');
    }
  };

  const handlePermissionSubmit = async () => {
    try {
      setPermissionConfirmLoading(true);
      const values = await permissionForm.validateFields();

      const createData: CreatePermissionRequest = {
        permission_name: values.permission_name,
        uri: values.uri,
        flag: values.flag,
      };
      if (devSearchId) {
        const response = await updatePermission({ ...createData, id: Number(devSearchId) });
        if (response.code === 0) {
          message.success('权限编辑成功');
          setIsPermissionModalOpen(false);
          permissionForm.resetFields();
          // 重新获取权限列表
          fetchPermissions();
        } else {
          message.error(response.message || '创建权限失败');
        }
        return;
      }

      const response = await createPermission(createData);
      if (response.code === 0) {
        message.success('权限创建成功');
        setIsPermissionModalOpen(false);
        permissionForm.resetFields();
        // 重新获取权限列表
        fetchPermissions();
      } else {
        message.error(response.message || '创建权限失败');
      }
    } catch (error) {
      console.error('权限表单验证失败:', error);
    } finally {
      setPermissionConfirmLoading(false);
    }
  };

  // 按模块分组权限（这里假设权限名称包含模块信息，实际可能需要调整）
  const groupedPermissions = permissions.reduce(
    (groups, permission) => {
      // 从权限名称中提取模块信息，这里使用简单的逻辑
      // 实际项目中可能需要权限有明确的模块字段

      const [module] = permission.flag.split(':');

      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(permission);
      return groups;
    },
    {} as Record<string, Permission[]>,
  );

  if (permissionsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载权限数据中...</div>
      </div>
    );
  }

  return (
    <div>
      <Flex justify="space-between" style={{ marginBottom: '16px' }}>
        <Title level={2}>角色管理</Title>
        <Space>
          {AppConfig.isDev && (
            <Button type="default" icon={<PlusOutlined />} onClick={handleAddPermission}>
              创建权限
            </Button>
          )}
          {AppConfig.isDev && (
            <Button type="default" icon={<PlusOutlined />} onClick={addPermissionAuto}>
              自动创建缺少的Token权限
            </Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加角色
          </Button>
        </Space>
      </Flex>

      <Table
        columns={columns}
        dataSource={roles}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      <Modal title={editingRole ? '编辑角色' : '添加角色'} open={isModalOpen} onOk={handleSubmit} onCancel={() => setIsModalOpen(false)} confirmLoading={confirmLoading} destroyOnHidden width={700}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="role_name"
            label="角色名称"
            rules={[
              { required: true, message: '请输入角色名称' },
              { max: 30, message: '角色名称不能超过30个字符' },
            ]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>

          <Form.Item name="describe" label="权限描述" rules={[{ required: true, message: '请输入权限描述' }]}>
            <Input.TextArea placeholder="请输入权限描述" />
          </Form.Item>

          <Form.Item name="permission_ids" label="权限设置" rules={[{ required: true, message: '请选择至少一个权限' }]}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const selectedPermissions = getFieldValue('permission_ids') || [];

                return (
                  <div>
                    {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
                      const modulePermissionIds = modulePermissions.map((p) => p.id);
                      const isAllSelected = modulePermissionIds.every((id) => selectedPermissions.includes(id));
                      const isIndeterminate = modulePermissionIds.some((id) => selectedPermissions.includes(id)) && !isAllSelected;

                      return (
                        <Card key={module} size="small" style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Checkbox
                              indeterminate={isIndeterminate}
                              checked={isAllSelected}
                              onChange={(e) => {
                                let newPermissions;

                                if (e.target.checked) {
                                  // 全选：添加当前模块所有权限
                                  newPermissions = [...new Set([...selectedPermissions, ...modulePermissionIds])];
                                } else {
                                  // 取消全选：移除当前模块所有权限
                                  newPermissions = selectedPermissions.filter((id: number) => !modulePermissionIds.includes(id));
                                }

                                setFieldsValue({
                                  permission_ids: newPermissions,
                                });
                              }}
                            />
                            <Text strong>{module}</Text>
                          </div>
                          <Divider style={{ margin: '12px 0' }} />
                          <Checkbox.Group
                            style={{ width: '100%' }}
                            value={selectedPermissions.filter((id: number) => modulePermissionIds.includes(id))}
                            onChange={(checkedValues) => {
                              // 保留其他模块的权限，只更新当前模块的权限
                              const otherModulePermissions = selectedPermissions.filter((id: number) => !modulePermissionIds.includes(id));
                              const newPermissions = [...otherModulePermissions, ...checkedValues];
                              setFieldsValue({
                                permission_ids: newPermissions,
                              });
                            }}
                          >
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
                              {modulePermissions.map((permission) => (
                                <Checkbox key={permission.id} value={permission.id} style={{ marginBottom: 8 }}>
                                  {permission.permission_name}

                                  {/* 下面仅用于开发人员且有权限的人员使用 */}
                                  {AppConfig.isDev && <DeleteOutlined style={{ color: 'red' }} onClick={() => devDeletePermission(permission)} />}
                                </Checkbox>
                              ))}
                            </div>
                          </Checkbox.Group>
                        </Card>
                      );
                    })}
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建权限弹窗 */}
      <Modal
        title="创建权限"
        open={isPermissionModalOpen}
        onOk={handlePermissionSubmit}
        onCancel={() => setIsPermissionModalOpen(false)}
        confirmLoading={permissionConfirmLoading}
        destroyOnHidden
        width={500}
      >
        <Form form={permissionForm} layout="vertical">
          <div>
            <Input
              prefix="ID"
              value={devSearchId}
              onChange={(e) => setDevSearchId(e.target.value)}
              suffix={
                <Button
                  type="link"
                  onClick={async () => {
                    const id = devSearchId;
                    const data = await getPermissionDetail({ id: Number(id) });
                    if (data.code) return message.error(data.message || '获取权限失败');
                    console.log('data', data, id);
                    permissionForm.setFieldsValue({
                      permission_name: data.data.permission_name,
                      uri: data.data.uri,
                      flag: data.data.flag,
                    });
                  }}
                >
                  查询-修改
                </Button>
              }
            />
          </div>
          <Form.Item
            name="permission_name"
            label="权限名称"
            rules={[
              { required: true, message: '请输入权限名称' },
              { max: 100, message: '权限名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入权限名称，如：用户管理" />
          </Form.Item>

          <Form.Item
            name="uri"
            label="权限URI"
            rules={[
              { required: true, message: '请输入权限URI' },
              { pattern: /^\/.*/, message: '权限URI必须以/开头' },
            ]}
          >
            <Input placeholder="请输入权限URI，如：/api/v1/users/*" />
          </Form.Item>

          <Form.Item name="flag" label="权限标识" rules={[{ required: true, message: '请输入权限标识' }, { message: '权限标识格式应为：模块:资源:操作，如：user:manage:create' }]}>
            <Input placeholder="请输入权限标识，如：user:manage:create" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
